import { useState, type KeyboardEvent } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import { useInspectHost } from './context';
import type { InspectSession } from './types';

/**
 * The keyboard and screen-reader way to point (INSPECTOR-SPEC §2, accessibility). Hidden until
 * focused, then a list of everything that can be pointed at, in reading order. Arrow keys move
 * (the outline follows), Enter selects, ⌘/Ctrl-Enter adds.
 */
export function TargetList({ session }: { session: InspectSession }) {
  const host = useInspectHost();
  const copy = useNamespaceCopy(inspectCopy);
  const [active, setActive] = useState(0);
  if (!host || session.state !== 'on' || !host.adapter.list) return null;
  const items = host.adapter.list();
  if (!items.length) return null;
  // The list can shrink (a re-render); the active row never points past its end.
  const current = Math.min(active, items.length - 1);
  const show = (index: number) => {
    const item = items[index];
    if (!item) return;
    const id = host.clock.current();
    host.setHover({
      candidate: {
        pid: item.targetId,
        kind: 'element',
        rects: host.adapter.rects(item.targetId, id),
        instanceRef: null,
      },
      label: item.label || null,
    });
  };
  const select = async (index: number, add: boolean) => {
    const item = items[index];
    if (!item) return;
    const id = host.request('click');
    if (!id) return;
    const resolved = await session
      .resolve(
        {
          pid: item.targetId,
          kind: 'element',
          rects: host.adapter.rects(item.targetId, id),
          instanceRef: null,
        },
        id,
      )
      .catch(() => null);
    if (!resolved || !host.clock.isCurrent(id, 'click')) return;
    host.choose(resolved.targetId, add || host.addMode ? 'add' : 'replace');
  };
  const onKey = (event: KeyboardEvent<HTMLUListElement>) => {
    const last = items.length - 1;
    const next =
      event.key === 'ArrowDown'
        ? Math.min(active + 1, last)
        : event.key === 'ArrowUp'
          ? Math.max(active - 1, 0)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next !== null) {
      event.preventDefault();
      setActive(next);
      show(next);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void select(current, event.metaKey || event.ctrlKey);
    }
  };
  const selected = new Set(session.targets.map((t) => t.nodeId));
  return (
    <ul
      className="pc-inspect-targets"
      role="listbox"
      tabIndex={0}
      aria-label={copy.targetsLabel}
      aria-multiselectable="true"
      aria-activedescendant={`pc-inspect-target-${current}`}
      data-testid="inspect-targets"
      onKeyDown={onKey}
      onFocus={() => show(current)}
      onBlur={() => host.setHover(null)}
    >
      {items.map((item, i) => (
        <li
          key={item.targetId}
          id={`pc-inspect-target-${i}`}
          role="option"
          aria-selected={selected.has(item.targetId)}
          data-active={i === current || undefined}
        >
          {item.label || copy.element}
        </li>
      ))}
    </ul>
  );
}
